















































































































import Vue from 'vue';
import axios from 'axios';
import MultiComplete from '@/components/MultiComplete.vue';
import XLSX from 'xlsx';
import { range } from '@/main';
import Tabs from "@/components/Tabs.vue";

const BASE_URL = (process.env.NODE_ENV === 'production') ? '/api' : '//localhost:8000';

const INT = Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 0, useGrouping: true });

interface APICustomerMap {
  pallets: {
    [name: string]: {
      [week: string]: number;
    };
  };
  comments: {
    [name: string]: {
      [week: string]: string;
    };
  };
  max_week: number;
}

interface CustomerMap {
  [name: string]: {
    [week: string]: {
      pallets: number;
      showNote: boolean;
      noteText: string;
      saving: boolean;
    };
  };
}

export default Vue.extend({
  name: 'CustomerWeeklyPallets',
  components: { MultiComplete, Tabs },
  data() {
    return {
      depots: [] as string[],
      customers: {} as CustomerMap,
      previousYear: {} as CustomerMap,
      selectedCustomers: [] as string[],
      spec: {
        depot: 'all',
        yearsAgo: 0,
        tier: "all" as "all" | "gold" | "silver" | "balance",
      },
      tabs: [
        {
          text: "All",
          value: "all",
        },
        {
          text: "Gold",
          value: "gold",
        },
        {
          text: "Silver",
          value: "silver",
        },
        {
          text: "Balance",
          value: "balance",
        },
      ],
      loading: true,
      note: 'foo',
      period: '*' as '1' | '2' | '3' | '4' | '*',
      maxWeek: 0,
    };
  },
  filters: {
    int(x: number) {
      if (x === 0) {
        return '-';
      }
      return INT.format(x);
    },
  },
  computed: {
    weeks(): string[] {
      const customers = Object.values(this.customers);
      if (customers.length === 0) {
        return [];
      } else {
        return Object.keys(customers[0]);
      }
    },
    selectedWeeks(): string[] {
      switch (this.period) {
        case '1':
          return range(1, 13).map((x) => x.toString());
        case '2':
          return range(14, 26).map((x) => x.toString());
        case '3':
          return range(27, 39).map((x) => x.toString());
        case '4':
          return range(40, 52).map((x) => x.toString());
        case '*':
          return range(1, this.maxWeek).map((x) => x.toString());
      }
    },
    filteredCustomers(): CustomerMap {
      if (this.selectedCustomers.length === 0) {
        return this.customers;
      }
      return Object.fromEntries(
          Object.entries(this.customers)
              .filter(([customer, _]) => this.selectedCustomers.includes(customer)),
      );
    },
  },
  methods: {
    totalYTD(customer: string, map: CustomerMap) {
      const cust = map[customer];
      if (!cust) {
        return 0;
      }
      let total = 0;
      for (const w of this.weeks.slice(0, this.weeks.length - 1)) {
        if (w in cust) {
          total += cust[w].pallets;
        }
      }
      return total;
    },
    averageYTD(customer: string, map: CustomerMap) {
      return this.totalYTD(customer, map) / (this.weeks.length - 1);
    },
    overallTotalPallets(week: string): number {
      return Object.values(this.filteredCustomers).reduce((acc, c) => acc + c[week]?.pallets ?? 0, 0);
    },
    overallTotalYTD() {
      let total = 0;
      for (const cust of Object.values(this.filteredCustomers)) {
        for (const w of this.weeks.slice(0, this.weeks.length - 1)) {
          total += cust[w].pallets;
        }
      }
      return total;
    },
    overallAverageYTD() {
      return this.overallTotalYTD() / (this.weeks.length - 1);
    },
    weekClass(customer: string, week: string) {
      const average = this.averageYTD(customer, this.customers);
      const thisWeek = this.customers[customer][week]?.pallets ?? 0;
      if (thisWeek > average) {
        return 'green';
      } else if (thisWeek >= 0.9 * average) {
        return 'amber';
      } else {
        return 'red';
      }
    },
    showNote(customer: string, week: string) {
      this.customers[customer][week].showNote = true;
    },
    async saveNote(e: Event, customer: string, week: string) {
      this.customers[customer][week].saving = true;
      try {
        const parent = (e.target as HTMLElement).parentElement;
        const editor = parent?.previousElementSibling;
        const comment = (editor as HTMLElement)?.innerText ?? '';
        await axios.put(`${BASE_URL}/reports/customer-weekly-pallets-comment/${customer}/${week}/`, comment, {
          headers: {
            'Content-Type': 'text/plain',
          },
        });
        this.customers[customer][week].noteText = comment;
      } finally {
        this.customers[customer][week].saving = false;
      }
      this.closeNote(customer, week);
    },
    closeNote(customer: string, week: string) {
      this.customers[customer][week].showNote = false;
    },
    download() {
      const wb = XLSX.utils.book_new();
      const sheet = XLSX.utils.table_to_sheet(this.$refs.report as HTMLTableElement);
      XLSX.utils.book_append_sheet(wb, sheet, 'Current Week');
      XLSX.writeFile(wb, `Customer Weekly Pallets.xlsx`);
    },
  },
  async mounted() {
    this.depots = (await axios.get<{ depots: string[] }>(`${BASE_URL}/reports/depots/`)).data.depots;
  },
  watch: {
    spec: {
      deep: true,
      immediate: true,
      async handler(spec: { depot: string, yearsAgo: number }) {
        this.loading = true;
        this.selectedCustomers = [];

        async function loadYear(offset: number) {
          const response = await axios.get<APICustomerMap>(`${BASE_URL}/reports/customer-weekly-pallets/`, {
            params: {
              ...spec,
              yearsAgo: spec.yearsAgo + offset,
            },
          });
          const customerData = response.data;
          const newCustomers = {} as CustomerMap;
          let weeks = [] as string[];
          for (const [customer, weeklyPallets] of Object.entries(customerData.pallets)) {
            newCustomers[customer] = {};
            if (weeks.length === 0) {
              weeks = Object.keys(weeklyPallets);
            }
            if (offset === 0) {
              vm.maxWeek = customerData.max_week;
            }
            for (const week of vm.selectedWeeks.filter((w) => parseInt(w, 10) <= vm.maxWeek)) {
              if (week === 'Total') {
                continue;
              }
              newCustomers[customer][week] = {
                pallets: weeklyPallets[week] ?? 0,
                showNote: false,
                noteText: customerData.comments[customer][week] ?? '',
                saving: false,
              };
            }
          }
          return newCustomers;
        }

        const vm = this;
        await Promise.all([
          (async () => vm.customers = await loadYear(0))(),
          (async () => this.previousYear = await loadYear(1))(),
        ]);
        this.loading = false;
      },
    },
  },
});
